import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SubscriptionsService } from 'src/app/services/subscriptions.service';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';

export interface AddonRenewalLine {
	subscriptionAddonId: number;
	addonCode: string;
	addonName: string;
	unitLabel: string;
	quantity: number;
	unitPrice: number;
	payableAmount: number;
	gstPercentage: number;
	gstAmount: number;
	finalPayable: number;
	currentEndDate: string;
	projectedEndDate: string;
	/** Whether the user wants to renew this addon in the upcoming cycle. */
	includeInRenewal: boolean;
}

export interface NewAddonSelection {
	addon: any;           // AddonMaster record from catalog
	quantity: number;
	proratedSubtotal: number;
	gstAmount: number;
	total: number;
}
@Component({
	selector: 'calsoft-renew-active',
	templateUrl: './renew-active.component.html',
	styleUrls: ['./renew-active.component.scss']
})
export class RenewActiveComponent implements OnInit, OnDestroy {

	@Input() plan: any;
	@Input() subscriptionSummary: any;
	@Input() daysRemaining = 0;
	@Input() renewalThresholdDays = 31;
	@Input() canRenew = false;
	@Input() loaded = false;
	@Input() pricingList: any[] = [];
	@Input() availableMonthlyPrice = 0;
	@Input() availableAnnualPrice = 0;
	@Input() pricingPreview: any;
	@Input() currentUsers = 0;
	@Input() isUnlimitedPlan = false;
	@Input() isFreePlan = false;
	@Input() showPaymentOptions = false;
	@Input() upgradePlantype = false;
	@Input() hasActiveSubscription = false;
	@Input() currencySymbol: string = '₹';

	@Output() renew = new EventEmitter<void>();
	@Output() changePlan = new EventEmitter<void>();
	@Output() addUsers = new EventEmitter<void>();
	@Output() addUsersProceed = new EventEmitter<any>();
	@Output() usersAdded = new EventEmitter<any>();
	@Output() manageSubscription = new EventEmitter<void>();
	@Output() paymentHistory = new EventEmitter<void>();
	@Output() editBilling = new EventEmitter<void>();
	@Output() changeBillingType = new EventEmitter<string>();
	@Output() renewWithAddons = new EventEmitter<any>();

	private readonly _onDestroy = new Subject<void>();
	private latestPaymentRow: any = null;

	addUserModalOpen = false;
	showAddonModal = false;
	addUserCheckoutInProgress = false;
	addUserCharge = 0;
	addUserTax = 0;
	addUserControl = new FormControl(1);

	// ---- Addon renewal state ----
	addonLines: AddonRenewalLine[] = [];
	excludedAddonLines: AddonRenewalLine[] = [];
	renewalPreview: any = null;
	renewalPreviewLoading = false;

	// ---- New add-on picker state ----
	availableAddons: any[] = [];
	availableAddonsLoading = false;
	showAddNewAddons = false;
	newAddonSelections: NewAddonSelection[] = [];

	/** Catalog addons filtered to PLAN type (max 1 per renewal). */
	get planTypeAddons(): any[] {
		return this.availableAddons.filter(a => this.isPlanType(a));
	}

	/** Catalog addons filtered to non-PLAN type (multiple allowed). */
	get otherTypeAddons(): any[] {
		return this.availableAddons.filter(a => !this.isPlanType(a));
	}

	/** True when a PLAN-type addon is already selected in newAddonSelections. */
	get planAddonSelected(): boolean {
		return this.newAddonSelections.some(s => this.isPlanType(s.addon));
	}

	/** Subtotal (excl. GST) for all newly selected addons. */
	get newAddonSubtotal(): number {
		return this.newAddonSelections.reduce((sum, s) => sum + (s.proratedSubtotal || 0), 0);
	}

	/** GST for all newly selected addons. */
	get newAddonGst(): number {
		return this.newAddonSelections.reduce((sum, s) => sum + (s.gstAmount || 0), 0);
	}

	/** Aggregated addon subtotal for selected (included) addons. */
	get selectedAddonSubtotal(): number {
		return this.addonLines
			.filter(a => a.includeInRenewal)
			.reduce((sum, a) => sum + (a.payableAmount || 0), 0);
	}

	get selectedAddonGst(): number {
		return this.addonLines
			.filter(a => a.includeInRenewal)
			.reduce((sum, a) => sum + (a.gstAmount || 0), 0);
	}

	get renewalPlanAmount(): number {
		return Number(this.renewalPreview?.planAmount || 0);
	}

	get renewalPlanGst(): number {
		return Number(this.renewalPreview?.planGstAmount || 0);
	}

	get renewalWalletCredit(): number {
		return Number(this.renewalPreview?.walletCredit || 0);
	}

	get renewalFinalPayable(): number {
		const subtotal = this.renewalPlanAmount + this.selectedAddonSubtotal + this.newAddonSubtotal;
		const gst = this.renewalPlanGst + this.selectedAddonGst + this.newAddonGst;
		return Math.max(0, subtotal + gst - this.renewalWalletCredit);
	}

	constructor(private subscriptionsService: SubscriptionsService,
		private router: Router,

	) { }

	ngOnInit(): void {
		this.addUserControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
			this.computeAddUserCharge();
		});
		this.computeAddUserCharge();
		// Skip payment history fetch for trial accounts — they have no real payments
		if (!this.isTrial) {
			this.loadLatestPaymentRow();
		}
		// loadAvailableAddons() is called inside loadRenewalPreview() after addonLines is
		// populated, so the "already active" filter works correctly.
		this.loadRenewalPreview();
		this.getLatestPaymentSubscription();
	}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	get subscriptionExpired(): boolean {
		const status = String(this.subscriptionSummary?.status || this.subscriptionSummary?.subscriptionMode || '').toUpperCase();
		return status === 'EXPIRED';
	}

	get isTrial(): boolean {
		const status = String(this.subscriptionSummary?.status || this.subscriptionSummary?.subscriptionMode || '').toUpperCase();
		return status === 'TRIAL';
	}

	get buttonLabel(): string {
		return this.subscriptionExpired ? 'Renew Now' : 'Renew Subscription';
	}

	get showPaymentCylce(): boolean {
		return this.hasActiveSubscription && this.availableMonthlyPrice > 0 && this.availableAnnualPrice > 0;
	}

	get currentBillingCycle(): string {
		const paymentCycle = String(this.currentPaymentRow?.billingCycle || '').toLowerCase();
		if (paymentCycle === 'monthly' || paymentCycle === 'annually') {
			return paymentCycle;
		}
		return String(this.subscriptionSummary?.billingCycle || '').toLowerCase() === 'monthly' ? 'monthly' : 'annually';
	}

	get displayPlanName(): string {
		return this.subscriptionSummary?.currentPlanName
			|| this.subscriptionSummary?.pricingName
			|| this.plan?.planName
			|| 'Subscription';
	}

	get displayUsers(): number {
		const paymentUsers = Number(this.currentPaymentRow?.finalUsers || this.currentPaymentRow?.users || 0);
		if (Number.isFinite(paymentUsers) && paymentUsers > 0) {
			return paymentUsers;
		}
		const users = Number(
			this.subscriptionSummary?.totalLicensedUsers
			|| this.subscriptionSummary?.activeUsers
			|| this.currentUsers
			|| 1
		);
		return Number.isFinite(users) && users > 0 ? users : 1;
	}



	get fallbackCycleUnitPrice(): number {
		// Use actual last-payment unit price from subscription summary (derived in buildPricingAmountSummary
		// from payment.planPricePerUser or subtotal/users) — not the PricingTab plan price.
		const explicitUnitPrice = Number(this.subscriptionSummary?.currentUnitPrice || 0);
		if (explicitUnitPrice > 0) {
			return explicitUnitPrice;
		}
		const paymentUnitPrice = Number(this.currentPaymentRow?.planPricePerUser || 0);
		if (paymentUnitPrice > 0) {
			return paymentUnitPrice;
		}
		const previewPerUser = Number(this.pricingPreview?.planPricePerUser || 0);
		if (previewPerUser > 0) {
			return previewPerUser;
		}
		const cyclePrice = this.currentBillingCycle === 'monthly'
			? Number(this.subscriptionSummary?.priceMonthly || this.availableMonthlyPrice || 0)
			: Number(this.subscriptionSummary?.priceAnnually || this.availableAnnualPrice || 0);
		return cyclePrice > 0 ? cyclePrice : Number(this.subscriptionSummary?.planPricePerUser || 0);
	}

	get currentSubtotal(): number {
		// Show actual last-payment subtotal (from buildPricingAmountSummary → payment.totalPrice),
		// not the PricingTab plan-price calculation.
		const explicitSubtotal = Number(this.subscriptionSummary?.currentPrice || 0);
		if (explicitSubtotal > 0) {
			return explicitSubtotal;
		}
		const paymentSubtotal = Number(this.currentPaymentRow?.totalPrice || this.currentPaymentRow?.subtotal || 0);
		if (paymentSubtotal > 0) {
			return paymentSubtotal;
		}
		const unitPrice = Number(this.fallbackCycleUnitPrice || 0);
		if (unitPrice > 0) {
			const quantity = this.isUnlimitedPlan ? 1 : this.displayUsers;
			return Number((unitPrice * quantity).toFixed(2));
		}
		return Number(
			this.subscriptionSummary?.subtotal
			|| this.subscriptionSummary?.netTotal
			|| 0
		);
	}

	get currentGstAmount(): number {
		const paymentGst = Number(this.currentPaymentRow?.taxAmount || this.currentPaymentRow?.gstAmount || this.currentPaymentRow?.currentGstAmount || 0);
		if (paymentGst > 0) {
			return paymentGst;
		}
		const explicitGst = Number(this.subscriptionSummary?.currentGstAmount || 0);
		if (explicitGst > 0) {
			return explicitGst;
		}

		return Number((this.currentSubtotal * 0.18).toFixed(2));
	}

	get currentGrandTotal(): number {
		const paymentGrandTotal = Number(this.currentPaymentRow?.grandTotal || this.currentPaymentRow?.amount || this.currentPaymentRow?.currentGrandTotal || 0);
		if (paymentGrandTotal > 0) {
			return paymentGrandTotal;
		}
		const explicitGrandTotal = Number(this.subscriptionSummary?.currentGrandTotal || 0);
		if (explicitGrandTotal > 0) {
			return explicitGrandTotal;
		}

		return Number((this.currentSubtotal + this.currentGstAmount).toFixed(2));
	}

	onRenew(): void {
		this.renew.emit();
	}

	onChangePlan(): void {
		this.changePlan.emit();
	}

	onAddUsers(): void {
		// Trial users have no active paid subscription — send them to plan selection to subscribe first.
		// Note: hasActiveSubscription is set to true for any non-EXPIRED account (including TRIAL),
		// so we must check subscriptionMode/status directly and not rely on hasActiveSubscription.
		const mode = String(this.subscriptionSummary?.subscriptionMode || this.subscriptionSummary?.status || '').toUpperCase();
		const isPaidActive = !!this.subscriptionSummary?.subscriptionActive;
		if (mode === 'TRIAL' || (!isPaidActive && mode !== 'PAID' && mode !== 'ACTIVE')) {
			this.router.navigate(['/subscription/plan-selection']);
			return;
		}
		this.addUserModalOpen = true;
		this.addUserCheckoutInProgress = false;
		this.computeAddUserCharge();
		this.addUsers.emit();
	}

	onManageSubscription(): void {
		this.router.navigate(['/paymentSubscription']);
	}

	onAddOns(): void {
		this.showAddonModal = true;
	}

	onPaymentHistory(): void {
		this.paymentHistory.emit();
	}

	onEditBilling(): void {
		this.editBilling.emit();
	}

	onChangeBillingType(type: string): void {
		this.changeBillingType.emit(type);
	}

	// ---- Addon renewal handlers ----

	onAddonIncludeChange(line: AddonRenewalLine, checked: boolean): void {
		line.includeInRenewal = checked;
		if (!checked) {
			// Immediately tell the backend to mark as EXCLUDED_FROM_RENEWAL
			this.subscriptionsService.removeAddonFromRenewal(line.subscriptionAddonId)
				.pipe(takeUntil(this._onDestroy))
				.subscribe({ error: () => { line.includeInRenewal = true; } });
		} else {
			// Re-load preview to get the addon back to ACTIVE if the user re-checks
			this.loadRenewalPreview();
		}
	}

	onProceedRenewalWithAddons(): void {
		const newAddonPayload = this.newAddonSelections.map(s => ({
			addonCode: s.addon.addonCode,
			quantity: s.quantity
		}));
		const payload: any = {
			paymentStatus: 'SUCCESS',
			paymentModeType: 'ONLINE'
		};
		if (newAddonPayload.length > 0) {
			payload.newAddons = newAddonPayload;
		}
		this.renewWithAddons.emit({
			payload,
			selections: this.newAddonSelections,
			preview: {
				planAmount: this.renewalPlanAmount,
				addonAmount: this.selectedAddonSubtotal,
				newAddonAmount: this.newAddonSubtotal,
				subtotal: this.renewalPlanAmount + this.selectedAddonSubtotal + this.newAddonSubtotal,
				gstAmount: this.renewalPlanGst + this.selectedAddonGst + this.newAddonGst,
				walletCredit: this.renewalWalletCredit,
				finalPayable: this.renewalFinalPayable,
				addons: this.addonLines.filter(a => a.includeInRenewal),
				newAddons: this.newAddonSelections.map(s => ({
					addonCode: s.addon.addonCode,
					addonName: s.addon.addonName,
					quantity: s.quantity,
					proratedSubtotal: s.proratedSubtotal,
					gstAmount: s.gstAmount,
					total: s.total
				}))
			}
		});
	}

	onAddUserControlBlur(): void {
		const val = Number(this.addUserControl.value);
		if (!val || val <= 0 || !Number.isInteger(val)) {
			this.addUserControl.setValue(1, { emitEvent: false });
		}
		this.computeAddUserCharge();
	}

	onAddUserKeydown(event: KeyboardEvent): void {
		if (event.key === '.' || event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
			event.preventDefault();
		}
	}

	onAddUserInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.value === '') {
			return; // allow clearing mid-edit; blur will reset if needed
		}
		const val = Number(input.value);
		if (val <= 0 || !Number.isInteger(val)) {
			input.value = '1';
			this.addUserControl.setValue(1, { emitEvent: true });
		}
	}

	onProceedToPay(): void {
		const addUserCount = this.normalizedAdditionalUsers();
		if (addUserCount <= 0) {
			return;
		}
		const payload = {
			addUserCount,
			addUserCharge: this.addUserCharge,
			addUserTax: this.addUserTax,
			preview: {
				subtotal: this.addUserCharge,
				gstAmount: this.addUserTax,
				grandTotal: Number((this.addUserCharge + this.addUserTax).toFixed(2)),
				additionalUsers: addUserCount,
				daysRemaining: this.daysRemaining
			}
		};
		this.addUserCheckoutInProgress = false;
		this.addUserModalOpen = false;
		this.addUsersProceed.emit(payload);
	}

	onAddUserPayment(event: any): void {
		this.usersAdded.emit({ response: event, updateUser: true });
	}

	private loadAvailableAddons(): void {
		this.availableAddonsLoading = true;
		this.subscriptionsService.getAvailableAddons()
			.pipe(takeUntil(this._onDestroy))
			.subscribe({
				next: (res: any) => {
					const all: any[] = Array.isArray(res) ? res : (res?.data || res?.content || []);
					// Filter out addons already active on this subscription
					this.availableAddons = all.filter(a =>
						!this.addonLines.some(l => l.addonCode === a.addonCode)
					);
					this.availableAddonsLoading = false;
				},
				error: () => { this.availableAddonsLoading = false; }
			});
	}

	isPlanType(addon: any): boolean {
		return String(addon?.addonType || '').toUpperCase() === 'PLAN';
	}

	/** Returns the cyclePrices entry matching the current billingCycle (case-insensitive). */
	getCyclePriceForAddon(addon: any): any {
		const cycle = String(this.subscriptionSummary?.billingCycle || '').toUpperCase();
		const prices: any[] = addon?.cyclePrices || [];
		return prices.find((p: any) => String(p.billingCycle || '').toUpperCase() === cycle)
			?? prices[0]
			?? null;
	}

	isAddonSelected(addonCode: string): boolean {
		return this.newAddonSelections.some(s => s.addon.addonCode === addonCode);
	}

	isPlanAddonDisabledForSelection(addon: any): boolean {
		// Disable a PLAN addon if another PLAN addon is already selected
		return this.isPlanType(addon) && this.planAddonSelected && !this.isAddonSelected(addon.addonCode);
	}

	onToggleNewAddon(addon: any, checked: boolean): void {
		if (checked) {
			const minQty = Number(addon.minQuantity || 1);
			const sel: NewAddonSelection = { addon, quantity: minQty, proratedSubtotal: 0, gstAmount: 0, total: 0 };
			this.computeNewAddonAmounts(sel);
			this.newAddonSelections = [...this.newAddonSelections, sel];
		} else {
			this.newAddonSelections = this.newAddonSelections.filter(s => s.addon.addonCode !== addon.addonCode);
		}
	}

	onNewAddonQuantityChange(sel: NewAddonSelection, qty: number): void {
		const minQty = Number(sel.addon.minQuantity || 1);
		const maxQty = sel.addon.maxQuantity ? Number(sel.addon.maxQuantity) : null;
		sel.quantity = Math.max(minQty, Number.isFinite(qty) && qty > 0 ? qty : minQty);
		if (maxQty !== null) {
			sel.quantity = Math.min(sel.quantity, maxQty);
		}
		this.computeNewAddonAmounts(sel);
		this.newAddonSelections = [...this.newAddonSelections]; // trigger change detection
	}

	private computeNewAddonAmounts(sel: NewAddonSelection): void {
		const cp = this.getCyclePriceForAddon(sel.addon);
		const unitPrice = Number(cp?.unitPrice ?? 0);
		const gstPct = Number(cp?.gstPercentage ?? 18);
		let daysRemaining = Number(this.daysRemaining || this.subscriptionSummary?.daysRemaining || 0);
		const billingCycle = String(this.subscriptionSummary?.billingCycle || '').toLowerCase();
		const cycleDays = billingCycle === 'monthly' ? 30 : 365;

		// If on the first day of the cycle, treat as full cycle
		if (daysRemaining >= cycleDays - 1) {
			daysRemaining = cycleDays;
		}

		const prorata = Math.min(1, Math.max(0, daysRemaining / Math.max(cycleDays, 1)));
		const subtotal = Number((unitPrice * sel.quantity * prorata).toFixed(2));
		const gst = Number((subtotal * gstPct / 100).toFixed(2));
		sel.proratedSubtotal = subtotal;
		sel.gstAmount = gst;
		sel.total = Number((subtotal + gst).toFixed(2));
	}

	private loadRenewalPreview(): void {
		this.renewalPreviewLoading = true;
		this.subscriptionsService.getRenewalWithAddonsPreview()
			.pipe(takeUntil(this._onDestroy))
			.subscribe({
				next: (res: any) => {
					this.renewalPreview = res;
					this.addonLines = (res?.addons || []).map((a: any) => ({
						subscriptionAddonId: a.subscriptionAddonId,
						addonCode: a.addonCode,
						addonName: a.addonName,
						unitLabel: a.unitLabel,
						quantity: a.quantity,
						unitPrice: a.unitPrice,
						payableAmount: a.payableAmount,
						gstPercentage: a.gstPercentage,
						gstAmount: a.gstAmount,
						finalPayable: a.finalPayable,
						currentEndDate: a.currentEndDate,
						projectedEndDate: a.projectedEndDate,
						includeInRenewal: true
					} as AddonRenewalLine));
					this.excludedAddonLines = (res?.excludedAddons || []).map((a: any) => ({
						...a,
						includeInRenewal: false
					} as AddonRenewalLine));
					this.renewalPreviewLoading = false;
					// Load catalog addons AFTER addonLines is set so the "already active" filter works
					this.loadAvailableAddons();
				},
				error: () => {
					this.renewalPreviewLoading = false;
					// Preview failed — still load catalog with no active-addon exclusions
					this.loadAvailableAddons();
				}
			});
	}

	private computeAddUserCharge(): void {
		const addUserCount = this.normalizedAdditionalUsers();
		let daysRemaining = Number(this.daysRemaining || this.subscriptionSummary?.daysRemaining || 0);
		const billingCycle = String(this.subscriptionSummary?.billingCycle || '').toLowerCase();
		const cycleDays = billingCycle === 'monthly' ? 30 : 365;

		// If on the first day of the cycle, treat as full cycle
		if (daysRemaining >= cycleDays - 1) {
			daysRemaining = cycleDays;
		}

		// Use planPricePerUser from backend preview (PricingTab) for accurate per-user pricing
		const perUserPrice = Number(this.pricingPreview?.planPricePerUser || 0)
			|| (billingCycle === 'monthly'
				? Number(this.availableMonthlyPrice || 0)
				: Number(this.availableAnnualPrice || 0));
		const prorata = Math.max(0, Math.min(1, daysRemaining / Math.max(cycleDays, 1)));
		const subtotal = perUserPrice * addUserCount * prorata;
		this.addUserCharge = Number(subtotal.toFixed(2));
		this.addUserTax = Number((subtotal * 0.18).toFixed(2));
	}

	private normalizedAdditionalUsers(): number {
		const raw = Number(this.addUserControl.value || 0);
		return Number.isFinite(raw) && raw > 0 ? raw : 0;
	}

	private get currentPaymentRow(): any {
		return this.latestPaymentRow || this.subscriptionSummary?.lastPaymentRaw || null;
	}

	private loadLatestPaymentRow(): void {
		// Fetch up to 10 records so we can skip addon-only entries (SUBSCRIPTION_WITH_ADDON)
		// and find the most recent main plan subscription record.
		this.subscriptionsService.getSubscriptionHistoryPaged(0, 10, 'id,desc')
			.pipe(takeUntil(this._onDestroy))
			.subscribe((response: any) => {
				const latest = this.extractLatestPaymentRow(response);
				if (!latest) {
					return;
				}
				this.latestPaymentRow = latest;
				if (!this.subscriptionSummary || typeof this.subscriptionSummary !== 'object') {
					this.subscriptionSummary = {};
				}
				this.subscriptionSummary.lastPaymentRaw = latest;
				this.patchSummaryFromPaymentRow(latest);
				this.computeAddUserCharge();
			});
	}

	private extractLatestPaymentRow(response: any): any {
		if (!response) return null;

		let rows: any[] = [];
		if (Array.isArray(response)) {
			rows = response;
		} else if (Array.isArray(response?.content)) {
			rows = response.content;
		} else if (Array.isArray(response?._embedded?.paymentSubscription)) {
			rows = response._embedded.paymentSubscription;
		} else if (typeof response === 'object' && response.id) {
			rows = [response];
		}

		if (rows.length === 0) return null;

		// 1. Find the anchor: the latest record that isn't just an addon purchase
		const anchor = rows.find(r => r.operationType !== 'SUBSCRIPTION_WITH_ADDON') || rows[0];

		// 2. Find all records in the same cycle (matching cycleStartDate)
		// We filter to non-addon records to avoid mixing plan totals with independent addon purchases
		const cycleRows = rows.filter(r =>
			r.cycleStartDate === anchor.cycleStartDate &&
			r.operationType !== 'SUBSCRIPTION_WITH_ADDON'
		);

		// 3. Aggregate totals for the cycle
		const aggregated = {
			...anchor,
			totalPrice: cycleRows.reduce((sum, r) => sum + (r.totalPrice || 0), 0),
			grandTotal: cycleRows.reduce((sum, r) => sum + (r.grandTotal || 0), 0),
			taxAmount: cycleRows.reduce((sum, r) => sum + (r.taxAmount || 0), 0),
			// Use the most recent user counts
			users: anchor.finalUsers || anchor.users,
			finalUsers: anchor.finalUsers || anchor.users
		};

		return aggregated;
	}

	private patchSummaryFromPaymentRow(latest: any): void {
		const lastPaymentAmount = latest.grandTotal || latest.totalPrice || latest.amount || latest.netTotal || latest.paymentAmount;
		if (lastPaymentAmount != null) {
			this.subscriptionSummary.lastPaymentAmount = Number(lastPaymentAmount);
		}

		const lastPaymentDate = latest.paymentDate || latest.createdOn || latest.createdDate || latest.txnDate;
		if (lastPaymentDate) {
			this.subscriptionSummary.lastPaymentDate = lastPaymentDate;
		}

		const currentSubtotal = latest.totalPrice || latest.subtotal || latest.currentPrice;
		const currentGstAmount = latest.taxAmount || latest.gstAmount || latest.currentGstAmount;
		const currentGrandTotal = latest.grandTotal || latest.amount || latest.currentGrandTotal;
		const rowUsers = latest.finalUsers || latest.users || this.subscriptionSummary.totalLicensedUsers || this.subscriptionSummary.activeUsers;
		const currentUnitPrice = latest.planPricePerUser
			|| ((currentSubtotal != null && rowUsers != null && Number(rowUsers) > 0)
				? (Number(currentSubtotal) / Number(rowUsers))
				: null);

		// Patch totalLicensedUsers so displayUsers fallback (used when currentPaymentRow
		// is filtered out, e.g. ADD_USERS) always reflects the latest known user count.
		if (rowUsers != null && Number(rowUsers) > 0) {
			this.subscriptionSummary.totalLicensedUsers = Number(rowUsers);
		}

		if (currentUnitPrice != null) {
			this.subscriptionSummary.currentUnitPrice = Number(currentUnitPrice);
		}
		if (currentSubtotal != null) {
			this.subscriptionSummary.currentPrice = Number(currentSubtotal);
		}
		if (currentGstAmount != null) {
			this.subscriptionSummary.currentGstAmount = Number(currentGstAmount);
		}
		if (currentGrandTotal != null) {
			this.subscriptionSummary.currentGrandTotal = Number(currentGrandTotal);
		}
		if (latest.billingCycle) {
			this.subscriptionSummary.billingCycle = latest.billingCycle;
		}
	}



	latestPaymentSubscription: any;

	getLatestPaymentSubscription(): void {
		this.subscriptionsService.getLatestPaymentSubscription().subscribe({
			next: (response) => {
				console.log('Latest Payment Subscription:', response);
				this.latestPaymentSubscription = response;
				if (response && response.content && response.content.length > 0) {
					const latest = response.content[0];
					this.latestPaymentRow = latest;
					if (!this.subscriptionSummary || typeof this.subscriptionSummary !== 'object') {
						this.subscriptionSummary = {};
					}
					this.patchSummaryFromPaymentRow(latest);
				}
			},
			error: (error) => {
				console.error('Error fetching payment subscription:', error);
			}
		});
	}
}